// StepEditPrompt — the tiny floating input the right-click edit ops open when
// they need text (add step, change callee, reassign owner). The universal
// ContextMenu is flat/onClick-only, so a text-carrying item stashes a prompt
// spec + pointer coords; this renders the input once per surface, portaled to
// <body> at those coords. Submit runs the spec (returns whether it applied →
// dismiss); Escape / blur cancels. Structure-only — the api's recompile
// invariant is the last guard.

import { useState, type ReactElement } from "react";
import { createPortal } from "react-dom";

export interface StepPromptSpec {
  readonly label: string;
  readonly initial: string;
  /** Commit the value; return true when applied (→ the prompt closes). */
  readonly run: (value: string) => boolean;
  /** Pointer coords (viewport) to anchor the input at. */
  readonly x: number;
  readonly y: number;
}

export function StepEditPrompt({
  spec,
  onClose,
}: {
  spec: StepPromptSpec | null;
  onClose: () => void;
}): ReactElement | null {
  if (!spec) return null;
  return createPortal(<PromptInput key={`${spec.x},${spec.y},${spec.label}`} spec={spec} onClose={onClose} />, document.body);
}

function PromptInput({ spec, onClose }: { spec: StepPromptSpec; onClose: () => void }) {
  const [value, setValue] = useState(spec.initial);
  return (
    <form
      className="fixed z-overlay"
      style={{ left: spec.x, top: spec.y }}
      data-step-edit-prompt=""
      onSubmit={(e) => {
        e.preventDefault();
        if (value.trim() === "") return;
        if (spec.run(value.trim())) onClose();
      }}
    >
      <input
        autoFocus
        value={value}
        placeholder={spec.label}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
        className="w-52 rounded-md border border-sky-500 bg-slate-900 px-2 py-1 text-xs text-slate-100 shadow-xl shadow-black/40 outline-none"
      />
    </form>
  );
}
